import React, { createRef, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'

import { DOMElementRect } from '@aytm/lib/common/func'
import { calculate } from '@aytm/app/stats/lib/colors'
import { COLORS_TOTAL, COLOR_METHOD_OPTIONS } from '@aytm/app/stats/page/sidebar/color-options/lib/constants'

import { Dropdown } from 'primereact/dropdown'

import ColorPickerPopup from 'lib/rfd-ui/components/advanced/color-picker-popup'

import style from '@aytm/app/stats/page/sidebar/color-options/styles/ColorPicker.module.scss'

const ColorPicker = ({ container, palette = [], onChange }) => {
  const [colors, setColors] = useState(palette.get('colors'))
  const [colorsRefs, setColorsRefs] = useState([])
  const [currentColor, setCurrentColor] = useState(null)
  const [currentColorId, setCurrentColorId] = useState(null)
  const [currentColorMethod, setCurrentColorMethod] = useState('*')

  const pickerRef = useRef(null)
  const colorPickerRef = useRef(null)

  const pickerTopRef = useRef(0)

  const onColorMethodChangeRef = useRef()
  onColorMethodChangeRef.current = event => {
    const value = event.value

    if (currentColorMethod != value) {
      setCurrentColorMethod(value)
      if ('*' !== value) onChange(calculate(colors.get(0), value, COLORS_TOTAL))
    }
  }
  const onColorsRandomRef = useRef()
  onColorsRandomRef.current = () => {
    onChange(
      calculate(
        '*' === currentColorMethod ? null : colors.get(0),
        '*' === currentColorMethod ? 'random' : currentColorMethod,
        COLORS_TOTAL
      )
    )
  }
  const onColorChangeRef = useRef()
  onColorChangeRef.current = value => {
    setCurrentColor(value.hex)
    if (0 === currentColorId && '*' !== currentColorMethod) {
      onChange(calculate(value.hex.toUpperCase(), currentColorMethod, COLORS_TOTAL))
    } else {
      setCurrentColorMethod('*')
      onChange(colors.set(currentColorId, value.hex.toUpperCase()))
    }
  }

  const onClose = () => {
    setCurrentColor(null)
    setCurrentColorId(null)
  }

  const clickHandler = useCallback(() => {
    if (!container) return

    if (pickerRef.current && null === pickerRef.current.offsetParent) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, pickerRef.current])

  const scrollHandler = useCallback(() => {
    if (!container) return

    const pickerRect = DOMElementRect(pickerRef.current)
    if (pickerRect.top != pickerTopRef.current) {
      const containerRect = DOMElementRect(container)
      const popupRect = DOMElementRect(colorPickerRef.current.popup)

      colorPickerRef.current.popup.style.top = `${Math.min(
        containerRect.height - popupRect.height,
        Math.max(0, popupRect.top + (pickerRect.top - pickerTopRef.current))
      )}px`
    }

    pickerTopRef.current = pickerRect.top
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, pickerRef.current, pickerTopRef.current, colorPickerRef.current])

  const onColorSelect = value => {
    setCurrentColor(colors.get(value))
    setCurrentColorId(value)
  }

  useEffect(() => setColors(palette.get('colors')), [palette])
  useEffect(() => {
    setColorsRefs(ref =>
      Array(colors.size)
        .fill()
        .map((_, i) => ref[i] || createRef())
    )
    if (null !== currentColorId) {
      setCurrentColor(colors.get(currentColorId))
    }
  }, [colors, currentColorId])

  useEffect(() => {
    if (null !== pickerRef.current) {
      const pickerRect = DOMElementRect(pickerRef.current)
      pickerTopRef.current = pickerRect.top
    }

    document.removeEventListener('click', clickHandler)
    document.removeEventListener('scroll', scrollHandler)

    if (null !== colorPickerRef.current) {
      document.addEventListener('click', clickHandler)
      document.addEventListener('scroll', scrollHandler)
    }

    return () => {
      document.removeEventListener('click', clickHandler)
      document.removeEventListener('scroll', scrollHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerRef.current, pickerTopRef.current, colorPickerRef.current])

  return (
    <div ref={pickerRef} className={style['palette-picker']}>
      <div className={style['palette-picker-colors']}>
        {colors.map((color, index) => (
          <span
            key={`palette-picker-color-${index}`}
            ref={colorsRefs[index]}
            className={`${style['palette-picker-colors-item']}${
              index == currentColorId ? ' ' + style['palette-picker-colors-item-current'] : ''
            }`}
            style={{ backgroundColor: color }}
            onClick={() => onColorSelect(index)}
          ></span>
        ))}
      </div>

      {0 === currentColorId ? (
        <div className={style['palette-picker-methods']}>
          <div className="p-fluid" style={{ width: '172px' }}>
            <Dropdown
              value={currentColorMethod}
              options={COLOR_METHOD_OPTIONS}
              optionValue="value"
              optionLabel="text"
              fixedDropdownDir="bottom"
              onChange={onColorMethodChangeRef.current}
            />
          </div>
          {0 > ['monochromatic', 'analogous'].indexOf(currentColorMethod) && (
            <span className={style['palette-picker-methods-random']} onClick={onColorsRandomRef.current}>
              <i className="icon-aytmfd-refresh-v2" />
            </span>
          )}
        </div>
      ) : null}

      {null !== currentColor && null !== currentColorId && (
        <ColorPickerPopup
          ref={colorPickerRef}
          container={container}
          target={colorsRefs[currentColorId].current}
          color={currentColor}
          pickerTopRef={-30}
          offsetLeft={260 - 24 * currentColorId}
          onChange={onColorChangeRef.current}
          onClose={onClose}
        />
      )}
    </div>
  )
}

ColorPicker.propTypes = {
  container: PropTypes.any,
  palette: PropTypes.instanceOf(Map),
  onChange: PropTypes.func
}

ColorPicker.displayName = 'ColorPicker'

export default ColorPicker
